import type { HTMLInputTypeAttribute, KeyboardEvent, MouseEvent } from "react";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { useTheme } from "styled-components";
import type { IconsaxSize } from "../Iconsax/Iconsax.types.js";
import { ICONSAX_SIZE } from "../Iconsax/Iconsax.types.js";
import { InputFeedback } from "../InputFeedback/InputFeedback.js";
import { Text } from "../Text/Text.js";
import { TextSize, TextWeight } from "../Text/Text.types.js";
import {
    AppendSpan,
    AppendSpanAbsolute,
    PrependSpan,
    PrependSpanAbsolute,
    StyledDivBody,
    StyledDivFlex,
    StyledDivWithBorder,
    StyledInput,
} from "./Input.style.js";
import type { DefaultInputProps, InputProps } from "./Input.types.js";

const ICON_SIZE_TO_ICONSAX: Record<NonNullable<InputProps["size"]>, IconsaxSize> = {
    small: ICONSAX_SIZE.SMALL,
    medium: ICONSAX_SIZE.LARGE,
    large: ICONSAX_SIZE.LARGE,
};

const defaultProps: DefaultInputProps = {
    size: "medium",
    variant: "glass",
};

const preventMathCaractersInNumberInput = (type: HTMLInputTypeAttribute | undefined, evt: KeyboardEvent<HTMLInputElement>): void => {
    if (type && type.toLowerCase() === "number" && ["e", "E", "+", "-", ".", ","].includes(evt.key)) {
        evt.preventDefault();
    }
};

// TODO: Rewrite this to extract the HTMLTextAreaElement part to a new waap-ui component
export const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>((props, ref) => {
    const {
        beforeElement,
        afterElement,
        size,
        status,
        label,
        prependElements,
        errorText,
        successText,
        appendElements,
        prependIcon: PrependIcon,
        appendIcon: AppendIcon,
        className,
        value,
        variant,
        maxRows,
        width,
        maxWidth,
        fontSize,
        fontWeight,
        textAlign,
        canBreakLine = false,
        isStatic,
        onChange,
        rows = 1,
        onKeyDown,
        autoFocus,
        selectAllOnClick = props.variant === "borderless",
        onClick,
        ...attributes
    } = {
        ...defaultProps,
        ...props,
    };
    const theme = useTheme();

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => (canBreakLine ? textareaRef.current! : inputRef.current!));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (isStatic) return;
        // @ts-ignore
        onChange?.(e);
    };

    const handleFieldClick = (e: MouseEvent<HTMLInputElement | HTMLTextAreaElement, globalThis.MouseEvent>) => {
        if (selectAllOnClick) e.currentTarget.select();
        onClick?.(e);
    };

    useEffect(() => {
        if (!props.canBreakLine) return;

        const textarea = textareaRef?.current;
        if (!textarea) return;

        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
    }, [value]);

    useEffect(() => {
        if (autoFocus) {
            inputRef.current?.focus();
        }
    }, [autoFocus]);

    return (
        <StyledInput
            size={size}
            width={width}
            status={status}
            hasPrependIcon={!!PrependIcon}
            hasAppendIcon={!!AppendIcon}
            className={className}
            disabled={attributes.disabled}
            variant={variant}
            maxRows={maxRows}
            fontSize={fontSize}
            fontWeight={fontWeight}
            textAlign={textAlign}
            isStatic={isStatic}
            maxWidth={maxWidth}
            value={value}
        >
            {typeof label === "string" ? (
                <Text weight={TextWeight.SEMIBOLD} size={TextSize["XX-SMALL"]} color={theme.grayscale[80]}>
                    {label}
                </Text>
            ) : (
                label
            )}
            <StyledDivWithBorder>
                {beforeElement}
                <StyledDivFlex>
                    <StyledDivBody canBreakLine={canBreakLine}>
                        {prependElements && <PrependSpan asPrependIcon={!!PrependIcon}>{prependElements}</PrependSpan>}
                        {PrependIcon && (
                            <PrependSpanAbsolute>
                                <PrependIcon size={ICON_SIZE_TO_ICONSAX[size]} />
                            </PrependSpanAbsolute>
                        )}
                        {canBreakLine && (
                            // @ts-ignore
                            <textarea
                                {...attributes}
                                rows={rows}
                                ref={textareaRef}
                                value={value}
                                onChange={handleChange}
                                onClick={handleFieldClick}
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus={autoFocus}
                            />
                        )}
                        {!canBreakLine && (
                            <input
                                {...attributes}
                                onChange={handleChange}
                                value={value}
                                ref={inputRef}
                                onClick={handleFieldClick}
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus={autoFocus}
                                onKeyDown={(evt) => {
                                    preventMathCaractersInNumberInput(attributes.type, evt);
                                    onKeyDown?.(evt);
                                }}
                            />
                        )}
                        {AppendIcon && (
                            <AppendSpanAbsolute>
                                <AppendIcon size={ICON_SIZE_TO_ICONSAX[size]} />
                            </AppendSpanAbsolute>
                        )}
                    </StyledDivBody>
                    {appendElements && <AppendSpan>{appendElements}</AppendSpan>}
                </StyledDivFlex>
                {afterElement}
            </StyledDivWithBorder>

            {status === "error" && errorText && <InputFeedback type="error">{errorText}</InputFeedback>}
            {status === "success" && successText && <InputFeedback type="success">{successText}</InputFeedback>}
        </StyledInput>
    );
});
